import { useEffect, useRef, useState } from "react";
import ChartTableComponent from "../components/charts/ChartTableComponent";
import { toast } from "sonner";
import { apiCall } from "@/api/apiCall";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import { useQueue } from "@/context/QueueContext";
import { processParams } from "../utils/paramsHelper";
import { useNavigate } from "react-router-dom";

const Linebarstable = ({ year = "", month = "", monthNumber = "", contract = "", page = null,isRefresh = false }) => {
  const { enqueue } = useQueue();
const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  const getDataType = () => {
    if (!page?.datasets || !page.datasets[0]) return 'TOW';
    const label = page.datasets[0].label.toUpperCase();
    if (label.includes('EO') || label.includes('EOD')) return 'CNS';
    if (label.includes('CLAMP')) return 'CLAMP';
    if (label.includes('TOW')) return 'TOW';
    return 'TOW';
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      

      const apiParams = processParams(page?.params || {}, { year, month, contract });

      const response = await enqueue(() => apiCall("GET", page?.api || "/boardpack/enforcement-monthly-trend", apiParams,navigate,false,false,isRefresh));

      if (!response?.success) {
        throw new Error(response?.message || "Failed to fetch data");
      }

      const { upper_table, data: percentData } = response;
      const dataType = getDataType();

      const empData = upper_table[dataType]?.emp || {};
      const depData = upper_table[dataType]?.dep || {};

      const years = Object.keys(empData).sort();
      
      const blueData = [];
      const yellowData = [];
      const yearRanges = [];
      const dynamicMonths = [];

      years.forEach(yearKey => {
        const yearMonths = Object.keys(empData[yearKey]);
        const monthCount = yearMonths.length;

        yearRanges.push({
          year: yearKey,
          span: monthCount
        });

        yearMonths.forEach(monthKey => {
          blueData.push(empData[yearKey][monthKey] ?? 0);
          yellowData.push(depData[yearKey][monthKey] ?? 0);
          dynamicMonths.push(monthKey);
        });
      });

      const floatingBarData = blueData.map((val, i) => [val, yellowData[i]]);

      const datasets = [
        {
          label: page?.datasets?.[0]?.label || "Emp",
          data: blueData,
          color: page?.datasets?.[0]?.color || '#1787B0',
          order: page?.datasets?.[0]?.order || 1,
          borderDash: page?.datasets?.[0]?.borderDash || [] 
        },
        {
          label: page?.datasets?.[1]?.label || "Dep",
          data: yellowData,
          color: page?.datasets?.[1]?.color || '#1787B0',
          order: page?.datasets?.[1]?.order || 3,
          borderDash: page?.datasets?.[1]?.borderDash || [] 
        }
      ];

      const barDatasets = [
        {
          label: page?.barDatasets?.[0]?.label || "",
          data: floatingBarData,
          color: page?.barDatasets?.[0]?.color || 'rgba(180,180,180,0.6)',
          order: page?.barDatasets?.[0]?.order || 2,
          hideInTable: page?.barDatasets?.[0]?.hideInTable ?? true
        }
      ];

      // ✅ Second table data - sirf display ke liye, Ramadan label ke liye nahi
      // Second table data
const secondTableRows = [];

const percentDataType = percentData?.[dataType];

if (percentDataType) {
  const percentYears = Object.keys(percentDataType).sort();
  
  percentYears.forEach(yearKey => {
    const yearMonths = Object.keys(percentDataType[yearKey] || {});
    const rowData = [];
    
    yearMonths.forEach(monthKey => {
      const val = percentDataType[yearKey]?.[monthKey];
      let displayValue = 0;
      if (val !== null && val !== undefined) {
        // Remove % symbol if present and convert to number
        if (typeof val === 'string' && val.includes('%')) {
          displayValue = parseFloat(val);
        } else if (typeof val === 'number') {
          displayValue = val;
        }
      }
      rowData.push(displayValue);
    });
    
    if (rowData.length > 0) {
      secondTableRows.push({ 
        label: yearKey, 
        data: rowData 
      });
    }
  });
}

      const secondTableData = {
        title: `% Deployed to Employed - ${dataType === 'CNS' ? 'EO/EOD\'s' : dataType === 'CLAMP' ? 'Clamp Drivers' : 'Tow Drivers'}`,
        rows: secondTableRows
      };

      // ✅ IMPORTANT: Ramadan label ke liye ALG data pass karo
      // Ye upper_table se lega (emp + dep ka sum ya jo bhi page mein define ho)
      const ramadanData = {
        // Ye upper_table ka data hai jo ChartTableComponent mein Ramadan label ke liye use hoga
        revenueData: blueData,  // Emp data (ya jo bhi main dataset ho)
        // Agar kuch aur chahiye to add karo
      };

      setChartData({
        datasets,
        barDatasets,
        secondTableData,
        yearRanges,
        dynamicMonths,
        ramadanData  // ✅ Add this for Ramadan
      });

    } catch (error) {
      console.error("Error processing data:", error);
      setError(error?.message || "Error loading chart data");
      toast.error("Error loading chart data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchData} />;
  }

  if (!chartData) {
    return <ErrorState error="No data available" onRetry={fetchData} />;
  }

  return (
    <ChartTableComponent
      title={page?.title || "Jeddah Airport Enforcement Performance – KPI"}
      subtitle={page?.subtitle || "Average Daily Deployed v Employed"}
      in_chart_content_line_1={page?.in_chart_content_line_1}
      in_chart_content_line_2={page?.in_chart_content_line_2}
      datasets={chartData.datasets}
      barDatasets={chartData.barDatasets}
      showBars={true}
      months={chartData.dynamicMonths}
      yearRanges={chartData.yearRanges}
      yAxisTitle={page?.yAxisLabel}
      showSecondTable={page?.showSecondTable ?? true}
      secondTableData={chartData.secondTableData}
      showRamadanRow={page?.showRamadanRow ?? true}
      ramadanborderColor={page?.ramadanBorderColor || "#FFA500"}
      // ✅ Pass Ramadan data explicitly
      ramadanRevenueData={chartData.ramadanData?.revenueData || []}
    />
  );
};

export default Linebarstable;